/**
 * Jeu d'icônes de la charte F1 « Pierre taillée » (A48, A50).
 *
 * Vectoriel **monochrome** : un tracé seul, `stroke="currentColor"`, trait de
 * 2,1 à jointures en onglet et extrémités carrées — l'arête nette d'une pierre
 * taillée, comme la maquette F1. L'icône prend donc la couleur du texte qui
 * l'entoure (or dans un jeton d'or, mousse dans un bouton d'achat…) et aucune
 * image binaire n'est livrée.
 *
 * Chaque fonction renvoie une **chaîne SVG** à poser dans un `innerHTML` : les
 * écrans de l'interface sont écrits en gabarits de chaînes, une icône s'y glisse
 * comme un mot. Le lot 1 les a posées dans le HUD de combat (or, son, abandon) et
 * le ruban de bannière, le lot 2 dans l'Accalmie, les lots 3 et 4 sur le titre,
 * les fins de partie et le panneau Dev : l'interface n'affiche plus aucun émoji.
 * Le lot G (v0.22.0) y ajoute le Larbin, dernier dessin pixel de l'interface.
 *
 * Module **pur** (aucun DOM) : testé dans `tests/ui/icons.test.ts`.
 */

/** Tracés des icônes, grille 24 × 24, trait seul (aucun remplissage). */
const PATHS = {
  /** Or : pièce octogonale frappée d'un losange. */
  gold: '<path d="M8 3h8l5 5v8l-5 5H8l-5-5V8z"/><path d="M12 7l4 5-4 5-4-5z"/>',
  /** Dé : cube vu de trois quarts. */
  die: '<path d="M12 2.5l8.5 4.8v9.4L12 21.5l-8.5-4.8V7.3z"/><path d="M3.5 7.3L12 12l8.5-4.7M12 12v9.5"/>',
  /** Sort : étoile à quatre branches. */
  spell: '<path d="M12 2.5l2.4 7.1 7.1 2.4-7.1 2.4L12 21.5l-2.4-7.1L2.5 12l7.1-2.4z"/>',
  /** Affixe : losange marqué d'une croix. */
  affix: '<path d="M12 2.5l7 9.5-7 9.5L5 12z"/><path d="M12 7.5v9M9 12h6"/>',
  /** Nœud de l'arbre : hexagone serti. */
  node: '<path d="M12 2.5l8.2 4.75v9.5L12 21.5l-8.2-4.75v-9.5z"/><path d="M12 9l2.6 1.5v3L12 15l-2.6-1.5v-3z"/>',
  /** Machine (Gachapon) : dôme sur socle, fente et levier. */
  machine:
    '<path d="M6 10.5a6 6 0 0 1 12 0"/><path d="M4.5 10.5h15V21h-15z"/><path d="M9 15.5h6M12 13.5v4M8 21v-2h8v2"/>',
  /** Son activé : haut-parleur et deux ondes. */
  sound: '<path d="M3.5 9h4l5-4v14l-5-4h-4z"/><path d="M16 9.2a4 4 0 0 1 0 5.6M18.6 6.6a7.6 7.6 0 0 1 0 10.8"/>',
  /** Son coupé : haut-parleur barré d'une croix. */
  soundOff: '<path d="M3.5 9h4l5-4v14l-5-4h-4z"/><path d="M16 9.5l5 5M21 9.5l-5 5"/>',
  /** Drapeau d'abandon : fanion fendu sur sa hampe. */
  flag: '<path d="M5.5 21.5v-19"/><path d="M5.5 3.5h12l-3 4.25 3 4.25h-12"/>',
  /** Fermer : croix. */
  close: '<path d="M5.5 5.5l13 13M18.5 5.5l-13 13"/>',
  /** Flèche vers la droite (« Jour 2 → », suite). */
  arrow: '<path d="M3.5 12h16"/><path d="M13.5 6l6 6-6 6"/>',
  /** Se relève : flèche circulaire (remplace le glyphe « ↻ »). */
  revive: '<path d="M19 12a7 7 0 1 1-2.05-4.95"/><path d="M19 4v4.5h-4.5"/>',
  /** Verrou (machine ou nœud verrouillés). */
  lock: '<path d="M7.5 11V8a4.5 4.5 0 0 1 9 0v3"/><path d="M5 11h14v10H5z"/><path d="M12 15v2.5"/>',
  /** Coche (acquis, équipé). */
  check: '<path d="M4.5 12.5l4.5 4.5 10.5-10.5"/>',
  // ── Lot 2 (Accalmie) : icônes génériques des nœuds de l'arbre, choisies par
  // mot-clé du nom ou de l'effet (`ui/treeGraph.ts`, `nodeIconName`).
  /** Chrono : cadran octogonal et aiguilles (durée, temps de retour). */
  clock: '<path d="M8.5 3.5h7l5 5v7l-5 5h-7l-5-5v-7z"/><path d="M12 7.5V12l3 2.5"/>',
  /** Cible : losange et mire (critique, précision). */
  target: '<path d="M12 3l9 9-9 9-9-9z"/><path d="M12 8.5l3.5 3.5-3.5 3.5-3.5-3.5z"/><path d="M12 1v4M12 19v4M1 12h4M19 12h4"/>',
  /** Gobelet : contenant évasé (gobelet, bol, seau, contenant). */
  cup: '<path d="M4.5 4.5h15l-2.5 16h-10z"/><path d="M6 10h12"/>',
  /** Écu : bouclier taillé (conservation, ce qui reste d'une run). */
  shield: '<path d="M12 2.5l8 3v6.5c0 4.5-3.5 7.5-8 9.5-4.5-2-8-5-8-9.5V5.5z"/><path d="M12 7v10"/>',
  // ── Lots 3 et 4 (titre, Notes de dev) : remplacent les derniers émojis et
  // glyphes de l'interface (🔊 du titre, ✕ et ↗ du panneau Dev).
  /** Jouer : pointe taillée vers la droite. */
  play: '<path d="M7 4l12 8-12 8z"/>',
  /** Livre ouvert (Notes de dev, documents embarqués). */
  book: '<path d="M12 6.5C10 5 7 4.5 3 4.5v14c4 0 7 .5 9 2 2-1.5 5-2 9-2v-14c-4 0-7 .5-9 2z"/><path d="M12 6.5v14"/>',
  /** Lien externe : flèche sortant d'un cadre (« Tableau de bord »). */
  external: '<path d="M10 4.5H4.5v15h15V14"/><path d="M14 4h6v6M20 4l-9 9"/>',
  // ── v0.38.1 : molette des réglages de l'Accalmie (menu des options).
  /** Molette : roue dentée facettée à huit dents, moyeu hexagonal. */
  gear:
    '<path d="M12 2.5l1.6 2.6 3-.7.7 3 2.6 1.6-1.4 2.8 1.4 2.8-2.6 1.6-.7 3-3-.7L12 21.5l-1.6-2.6-3 .7-.7-3-2.6-1.6 1.4-2.8-1.4-2.8 2.6-1.6.7-3 3 .7z"/>' +
    '<path d="M12 8.5l3 1.75v3.5L12 15.5l-3-1.75v-3.5z"/>',
  // ── v0.22.0 (A58) : remplace le lutin pixel du bouton *Larbin* (HUD).
  /** Larbin : petit diablotin facetté — tête à cornes, yeux plissés, buste et bras. */
  minion:
    '<path d="M6.5 6.5L9 3l1.5 2.8h3L15 3l2.5 3.5v4.5l-3 3h-5l-3-3z"/>' +
    '<path d="M9.2 8.8l1.8 1.2M14.8 8.8L13 10"/>' +
    '<path d="M9.5 14l-1.5 7h8l-1.5-7M8.5 16.5L5 19M15.5 16.5L19 19"/>',
  // ── v0.26.0 (A72-17) : bouton « Lancer de tractopelle » du HUD.
  /** Tractopelle : pelle mécanique facettée — chenille, cabine, bras et godet. */
  backhoe:
    '<path d="M2.5 17.5h11l1.5 2-1.5 2h-11L1 19.5z"/>' +
    '<path d="M3 17.5V12h3.5L8 8h4.5v9.5M8 12h4.5"/>' +
    '<path d="M12.5 11.5L16.5 4l5 4.5"/>' +
    '<path d="M21.5 8.5l1 5h-4.5z"/>',
  // ── Phase 7 (A87) : classements.
  /** Médaille : ruban taillé et disque octogonal (rangs 1 à 3 des classements). */
  medal:
    '<path d="M8 2.5h8l-2.5 6.5h-3z"/>' +
    '<path d="M9 10.5h6l3.5 3.5v3.5L15 21H9l-3.5-3.5V14z"/>' +
    '<path d="M12 13.5v5"/>',
  /** Coupe : trophée facetté (bouton « Classements »). */
  trophy:
    '<path d="M7 3.5h10v6.5a5 5 0 0 1-10 0z"/>' +
    '<path d="M7 5.5H3.5v2.5a3.5 3.5 0 0 0 3.5 3.5M17 5.5h3.5v2.5a3.5 3.5 0 0 1-3.5 3.5"/>' +
    '<path d="M12 15v3.5M8 21h8"/>',
  // ── v0.46.0 (A94) : mode infini.
  /** Abîme : entonnoir facetté dont les gradins se resserrent vers le fond. */
  abyss: '<path d="M3.5 4.5h17M6 9.5h12M9 14.5h6M11.5 19.5h1"/><path d="M3.5 4.5L9 14.5M20.5 4.5L15 14.5"/>',
  /** Forge : enclume facettée posée sur son billot. */
  forge: '<path d="M3 8.5h12.5l5.5 3.5h-5l-2 3h-6l-1.5-3H3z"/><path d="M8.5 15v3.5h7V15M7 21.5h10"/>',
} as const;

/** Nom d'une icône du jeu. */
export type IconName = keyof typeof PATHS;

/** Liste des icônes disponibles, dans l'ordre de déclaration. */
export const ICON_NAMES = Object.keys(PATHS) as IconName[];

export interface IconOptions {
  /** Classe(s) CSS ajoutée(s) à `ic` (taille, couleur de contexte). */
  className?: string;
  /**
   * Libellé accessible. Absent (cas courant) : l'icône est **décorative**
   * (`aria-hidden`), le bouton ou le texte voisin porte le sens. Présent :
   * l'icône devient une image nommée (`role="img"` + `<title>`).
   */
  label?: string;
}

/** Échappe un texte pour un attribut ou un nœud texte HTML. */
function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/** Garde seulement des noms de classes plausibles (aucune injection d'attribut). */
function cleanClassName(className: string): string {
  return className
    .split(/\s+/)
    .filter((c) => /^[A-Za-z_-][\w-]*$/.test(c))
    .join(' ');
}

/**
 * Chaîne SVG d'une icône. La taille vient du CSS (`.ic`, 18 px par défaut), la
 * couleur du texte courant ; le trait (2,1, onglet, carré) est fixé ici pour que
 * l'icône garde sa matière même hors de la feuille de style.
 */
export function icon(name: IconName, opts: IconOptions = {}): string {
  const extra = opts.className ? cleanClassName(opts.className) : '';
  const cls = extra ? `ic ic-${name} ${extra}` : `ic ic-${name}`;
  const a11y = opts.label
    ? `role="img" aria-label="${escapeHtml(opts.label)}"`
    : 'aria-hidden="true" focusable="false"';
  const title = opts.label ? `<title>${escapeHtml(opts.label)}</title>` : '';
  return (
    `<svg class="${cls}" viewBox="0 0 24 24" ${a11y} fill="none" stroke="currentColor" ` +
    `stroke-width="2.1" stroke-linejoin="miter" stroke-linecap="square">${title}${PATHS[name]}</svg>`
  );
}

/** Vrai si `name` désigne une icône du jeu (garde de type pour les données). */
export function isIconName(name: string): name is IconName {
  return Object.prototype.hasOwnProperty.call(PATHS, name);
}
